import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthSessionChangedError, createAuthSessionCoordinator, sameAdministrativeSession } from '../lib/authSessionCoordinator';
import { activateGuardOfflineAccount, detachGuardOfflineAccount } from '../services/guardOfflineStore';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LAST_ACTIVITY_KEY = 'ar_last_activity_at';
const SESSION_IDLE_MS = 8 * 60 * 60 * 1000;
const ACTIVE_REFRESH_WINDOW_MS = 30 * 60 * 1000;
const USER_CACHE_KEY = 'ar_authenticated_user_cache';

const clearCouncilResidentBridge = () => {
  try {
    const residentKey = window.sessionStorage.getItem('ar:council-resident-storage-key');
    if (residentKey) window.sessionStorage.removeItem(residentKey);
    ['ar:council-resident-storage-key', 'ar:council-home-path', 'ar:council-return-path', 'ar:resident-login-handoff']
      .forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    // El cierre administrativo debe continuar aun si el navegador bloquea storage.
  }
};

const AuthContext = createContext(null);

const DEFAULT_EFFECTIVE_MODULE_PERMISSION = {
  enabled: false,
  base_level: 'none',
  user_override_level: null,
  effective_level: 'none',
  can_view: false,
  can_operate: false,
  can_manage: false,
};

const DEFAULT_EFFECTIVE_PERMISSIONS = {
  global_permissions: {
    is_direccion: false,
    access_level: 'none',
  },
  by_condominio: {},
};

const DEFAULT_UI_CONTEXT = {
  default_condominio_id: null,
  available_condominios_ids: [],
  available_modulos: [],
};

const normalizeUserPayload = (rawUser) => {
  if (!rawUser) return null;

  return {
    ...rawUser,
    roles: Array.isArray(rawUser.roles) ? rawUser.roles : [],
    condominios_ids: Array.isArray(rawUser.condominios_ids) ? rawUser.condominios_ids : [],
    condominios: Array.isArray(rawUser.condominios) ? rawUser.condominios : [],
    module_permissions: Array.isArray(rawUser.module_permissions) ? rawUser.module_permissions : [],
    schedules: Array.isArray(rawUser.schedules) ? rawUser.schedules : [],
    effective_permissions: {
      ...DEFAULT_EFFECTIVE_PERMISSIONS,
      ...(rawUser.effective_permissions || {}),
      global_permissions: {
        ...DEFAULT_EFFECTIVE_PERMISSIONS.global_permissions,
        ...(rawUser.effective_permissions?.global_permissions || {}),
      },
      by_condominio:
        rawUser.effective_permissions?.by_condominio &&
        typeof rawUser.effective_permissions.by_condominio === 'object' &&
        !Array.isArray(rawUser.effective_permissions.by_condominio)
          ? rawUser.effective_permissions.by_condominio
          : {},
    },
    ui_context: {
      ...DEFAULT_UI_CONTEXT,
      ...(rawUser.ui_context || {}),
      available_condominios_ids: Array.isArray(rawUser.ui_context?.available_condominios_ids)
        ? rawUser.ui_context.available_condominios_ids
        : [],
      available_modulos: Array.isArray(rawUser.ui_context?.available_modulos)
        ? rawUser.ui_context.available_modulos
        : [],
    },
  };
};

// Use localStorage instead of cookies for better browser compatibility
const storage = {
  get: (key) => localStorage.getItem(key),
  set: (key, value) => localStorage.setItem(key, value),
  remove: (key) => localStorage.removeItem(key),
};

const readableAuthError = (detail) => {
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const message = detail.map((item) => item?.msg).filter(Boolean).join('. ');
    if (message) return message;
  }
  return 'No fue posible iniciar sesión. Revisa tus datos e intenta otra vez.';
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authToken, setAuthToken] = useState(() => storage.get('access_token'));
  const [activeCondominioId, setActiveCondominioIdState] = useState(null);
  const mountedRef = useRef(true);
  const guardUserIdRef = useRef(null);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; coordinatorRef.current?.invalidatePending(); }; }, []);
  const coordinatorRef = useRef(null);
  if (!coordinatorRef.current) coordinatorRef.current = createAuthSessionCoordinator({
    storage,
    post: (path, data, config) => axios.post(`${API_URL}/api${path}`, data, config),
    onTokens: (accessToken) => { if (mountedRef.current) setAuthToken(accessToken); },
    onClear: () => {
      clearCouncilResidentBridge();
      const previousGuardUserId = guardUserIdRef.current;
      guardUserIdRef.current = null;
      detachGuardOfflineAccount(previousGuardUserId).catch(() => {});
      if (!mountedRef.current) return;
      setAuthToken(null); setUser(null); setActiveCondominioIdState(null); setLoading(false);
    },
  });
  const sessionController = coordinatorRef.current;
  const getSessionRevision = sessionController.revision;
  const isSessionCurrent = sessionController.isCurrent;
  const api = useMemo(() => axios.create({
    baseURL: `${API_URL}/api`,
    headers: { 'Content-Type': 'application/json' },
    // Ninguna pantalla debe permanecer indefinidamente en blanco si un
    // servicio tarda o deja de responder. Cada módulo conserva su propio
    // estado recuperable y permite reintentar sin perder la captura.
    timeout: 20000,
  }), []);
  const performRefresh = useCallback(() => sessionController.refresh(), [sessionController]);

  useEffect(() => {
    const requestId = api.interceptors.request.use((config) => {
      if (config._authRevision != null && !sessionController.isCurrent(config._authRevision)) throw new AuthSessionChangedError();
      config._authRevision = sessionController.revision();
      const currentToken = storage.get('access_token');
      if (currentToken) config.headers.Authorization = `Bearer ${currentToken}`;
      else delete config.headers.Authorization;
      return config;
    });
    const responseId = api.interceptors.response.use((response) => {
      if (!sessionController.isCurrent(response.config._authRevision)) throw new AuthSessionChangedError();
      return response;
    }, async (failure) => {
      const original = failure.config;
      if (!original) return Promise.reject(failure);
      const expectedRevision = original._authRevision;
      if (!sessionController.isCurrent(expectedRevision)) return Promise.reject(new AuthSessionChangedError());
      const method = String(original.method || 'get').toLowerCase();
      const transientStatus = failure.response?.status;
      const transientFailure = !failure.response || [502, 503, 504].includes(transientStatus);
      const transientRetryLimit = failure.code === 'ECONNABORTED' ? 1 : 4;
      if (['get', 'head', 'options'].includes(method) && transientFailure && (original._transientRetries || 0) < transientRetryLimit) {
        original._transientRetries = (original._transientRetries || 0) + 1;
        await new Promise((resolve) => window.setTimeout(resolve, original._transientRetries * 1200));
        if (!sessionController.isCurrent(expectedRevision)) return Promise.reject(new AuthSessionChangedError());
        return api(original);
      }
      if (failure.response?.status === 401) {
        if (storage.get('refresh_token') && !original._retry) {
          original._retry = true;
          try {
            const refreshed = await performRefresh();
            if (!sessionController.isCurrent(expectedRevision)) throw new AuthSessionChangedError();
            if (refreshed?.access_token) {
              original.headers.Authorization = `Bearer ${refreshed.access_token}`;
              return api(original);
            }
            if (!storage.get('refresh_token')) sessionController.clearIfCurrent(expectedRevision);
          } catch (refreshError) {
            if (refreshError.response?.status === 401) sessionController.clearIfCurrent(expectedRevision);
            return Promise.reject(refreshError);
          }
        } else sessionController.clearIfCurrent(expectedRevision);
      }
      return Promise.reject(failure);
    });
    return () => { api.interceptors.request.eject(requestId); api.interceptors.response.eject(responseId); };
  }, [api, performRefresh, sessionController]);

  const refreshAccessToken = useCallback(async () => {
    const expectedRevision = sessionController.revision();
    try { return (await performRefresh())?.access_token || null; }
    catch (failure) {
      if (failure.response?.status === 401) sessionController.clearIfCurrent(expectedRevision);
      return null;
    }
  }, [performRefresh, sessionController]);
  const refreshSession = useCallback(async () => Boolean(await refreshAccessToken()), [refreshAccessToken]);

  const fetchUser = useCallback(async (expectedRevision = sessionController.revision()) => {
    try {
      if (!sessionController.isCurrent(expectedRevision) || !mountedRef.current) return null;
      const token = storage.get('access_token');
      if (!token) {
        setAuthToken(null);
        setLoading(false);
        return;
      }
      setAuthToken(token);
      const response = await api.get('/auth/me');
      if (!sessionController.isCurrent(expectedRevision) || !mountedRef.current) return null;
      const normalizedUser = normalizeUserPayload(response.data);
      if (guardUserIdRef.current && guardUserIdRef.current !== normalizedUser?.id) {
        detachGuardOfflineAccount(guardUserIdRef.current).catch(() => {});
      }
      guardUserIdRef.current = normalizedUser?.id || null;
      if (normalizedUser?.id) activateGuardOfflineAccount(normalizedUser.id);
      setUser(normalizedUser);
      storage.set(USER_CACHE_KEY, JSON.stringify(normalizedUser));
      const storageKey = normalizedUser?.id ? `ar_active_condominio:${normalizedUser.id}` : null;
      const savedCondominioId = storageKey ? storage.get(storageKey) : null;
      const availableIds = normalizedUser?.ui_context?.available_condominios_ids || normalizedUser?.condominios_ids || [];
      const defaultCondominioId = normalizedUser?.ui_context?.default_condominio_id || availableIds[0] || null;
      // El selector global obtiene su catálogo autorizado desde /api/condominios.
      // El contexto no debe descartar aquí una selección persistida sólo porque
      // /auth/me no incluya la bandera is_direccion o el catálogo completo.
      setActiveCondominioIdState(savedCondominioId || defaultCondominioId);
      return normalizedUser;
    } catch (err) {
      if (!sessionController.isCurrent(expectedRevision) || !mountedRef.current) return null;
      console.error('Error fetching user:', { status: err.response?.status || null, code: err.code || err.name });
      if (err.response?.status === 401) {
        sessionController.clearIfCurrent(expectedRevision);
        return null;
      }
      const cachedUser = normalizeUserPayload(JSON.parse(storage.get(USER_CACHE_KEY) || 'null'));
      if (cachedUser) {
        setUser(cachedUser);
        const availableIds = cachedUser.ui_context?.available_condominios_ids || cachedUser.condominios_ids || [];
        setActiveCondominioIdState(cachedUser.ui_context?.default_condominio_id || availableIds[0] || null);
        return cachedUser;
      }
      return null;
    } finally {
      if (sessionController.isCurrent(expectedRevision) && mountedRef.current) setLoading(false);
    }
  }, [api, sessionController]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== null && !['access_token', 'refresh_token'].includes(event.key)) return;
      const current = sessionController.externalChange();
      if (!current.access_token || !current.refresh_token) {
        clearCouncilResidentBridge();
        const previousGuardUserId = guardUserIdRef.current;
        guardUserIdRef.current = null;
        detachGuardOfflineAccount(previousGuardUserId).catch(() => {});
        setUser(null); setActiveCondominioIdState(null); setAuthToken(null); setLoading(false); return;
      }
      if (!sameAdministrativeSession(authToken, current.access_token)) {
        setUser(null); setActiveCondominioIdState(null); setLoading(true); storage.remove(USER_CACHE_KEY);
      }
      setAuthToken(current.access_token);
      fetchUser(sessionController.revision());
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [fetchUser, sessionController, authToken]);

  useEffect(() => {
    let lastPersistedActivity = Number(storage.get(LAST_ACTIVITY_KEY) || 0);
    const registerActivity = () => {
      const now = Date.now();
      if (now - lastPersistedActivity < 60 * 1000) return;
      lastPersistedActivity = now;
      storage.set(LAST_ACTIVITY_KEY, String(now));
    };
    const shouldRefresh = () => {
      const lastActivity = Number(storage.get(LAST_ACTIVITY_KEY) || 0);
      return document.visibilityState === 'visible' && lastActivity > 0 && Date.now() - lastActivity < ACTIVE_REFRESH_WINDOW_MS;
    };
    const keepAlive = window.setInterval(() => {
      if (shouldRefresh()) refreshSession();
    }, 10 * 60 * 1000);
    const refreshOnFocus = () => {
      if (shouldRefresh()) refreshSession();
    };
    ['pointerdown', 'keydown', 'touchstart'].forEach((eventName) => window.addEventListener(eventName, registerActivity, { passive: true }));
    document.addEventListener('visibilitychange', refreshOnFocus);
    window.addEventListener('focus', refreshOnFocus);
    return () => {
      window.clearInterval(keepAlive);
      ['pointerdown', 'keydown', 'touchstart'].forEach((eventName) => window.removeEventListener(eventName, registerActivity));
      document.removeEventListener('visibilitychange', refreshOnFocus);
      window.removeEventListener('focus', refreshOnFocus);
    };
  }, [refreshSession]);

  const login = async (email, password, mfaCode = '') => {
    setError(null);
    const expectedRevision = sessionController.beginLogin();
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password, mfa_code: mfaCode || null });
      if (!sessionController.acceptLogin(response.data, expectedRevision)) return false;
      return await fetchUser(expectedRevision);
    } catch (failure) {
      if (sessionController.isCurrent(expectedRevision) && mountedRef.current) setError(readableAuthError(failure.response?.data?.detail));
      return false;
    }
  };

  const logout = async () => {
    try {
      const result = await sessionController.logout();
      if (sessionController.isCurrent(result.closedRevision) && mountedRef.current && result.data?.full_session_revoked === false) {
        toast.warning(result.data.message || 'Se cerró el acceso de este dispositivo. Actualiza la app para confirmar el cierre completo.');
      }
      return result.data;
    } catch (failure) {
      if (sessionController.isCurrent(failure.closedRevision) && mountedRef.current) {
        toast.warning('Se cerró este dispositivo. No se pudo confirmar el cierre en el servidor.');
      }
      return { full_session_revoked: false };
    }
  };

  const hasRole = (role) => {
    if (!user) return false;
    return user.roles?.some((r) => r.role === role && r.is_active);
  };

  const isDireccion = () => Boolean(
    user?.is_direccion
    || user?.roles?.some((role) => (
      typeof role === 'string'
        ? role === 'direccion'
        : role?.role === 'direccion' && role?.is_active !== false
    ))
  );
  
  const isSupervisor = () => {
    if (!user) return false;
    return user.roles?.some((r) => r.role === 'supervisor' && r.is_active);
  };
  
  const isAdministrador = () => {
    if (!user) return false;
    return user.roles?.some((r) => r.role === 'administrador' && r.is_active);
  };

  const hasCondominioAccess = (condominioId) => {
    if (!user) return false;
    if (isDireccion()) return true;
    return user.condominios_ids?.includes(condominioId);
  };

  const getSafeEffectivePermissions = () => user?.effective_permissions || DEFAULT_EFFECTIVE_PERMISSIONS;

  const getSafeUiContext = () => user?.ui_context || DEFAULT_UI_CONTEXT;

  const getEffectiveModulePermission = (condominioId, modulo) => {
    if (!user || !modulo) return DEFAULT_EFFECTIVE_MODULE_PERMISSION;

    const effectivePermissions = getSafeEffectivePermissions();
    const globalPermissions = effectivePermissions.global_permissions || DEFAULT_EFFECTIVE_PERMISSIONS.global_permissions;

    if (globalPermissions.is_direccion || globalPermissions.access_level === 'manage' || isDireccion()) {
      return {
        enabled: true,
        base_level: 'manage',
        user_override_level: null,
        effective_level: 'manage',
        can_view: true,
        can_operate: true,
        can_manage: true,
      };
    }

    if (!condominioId) return DEFAULT_EFFECTIVE_MODULE_PERMISSION;

    const byCondominio = effectivePermissions.by_condominio || {};
    const condominioPermissions = byCondominio[condominioId];
    const modulePermission = condominioPermissions?.modulos?.[modulo];

    return modulePermission
      ? { ...DEFAULT_EFFECTIVE_MODULE_PERMISSION, ...modulePermission }
      : DEFAULT_EFFECTIVE_MODULE_PERMISSION;
  };

  const canViewModule = (condominioId, modulo) => getEffectiveModulePermission(condominioId, modulo).can_view;

  const canOperateModule = (condominioId, modulo) => getEffectiveModulePermission(condominioId, modulo).can_operate;

  const canManageModule = (condominioId, modulo) => getEffectiveModulePermission(condominioId, modulo).can_manage;

  const setActiveCondominioId = useCallback((condominioId) => {
    if (!user || !condominioId) return false;
    // La lista visible ya proviene de un endpoint autenticado y el backend
    // vuelve a validar el acceso en cada operación. Repetir aquí una lista
    // incompleta dejaba bloqueado el selector para Dirección y algunos roles.
    storage.set(`ar_active_condominio:${user.id}`, condominioId);
    setActiveCondominioIdState(condominioId);
    return true;
  }, [user]);

  const getDefaultCondominioId = () => {
    if (!user) return null;
    const uiContext = getSafeUiContext();
    return activeCondominioId || uiContext.default_condominio_id || user.condominios_ids?.[0] || null;
  };

  const getAvailableModulos = () => {
    if (!user) return [];

    const effectivePermissions = getSafeEffectivePermissions();
    const uiContext = getSafeUiContext();

    if (effectivePermissions.global_permissions?.is_direccion || isDireccion()) {
      return Array.isArray(uiContext.available_modulos) ? uiContext.available_modulos : [];
    }

    return Array.isArray(uiContext.available_modulos) ? uiContext.available_modulos : [];
  };

  const getUserSchedules = () => (Array.isArray(user?.schedules) ? user.schedules : []);

  const value = useMemo(() => ({
    user,
    loading,
    error,
    login,
    logout,
    api,
    hasRole,
    isDireccion,
    isSupervisor,
    isAdministrador,
    hasCondominioAccess,
    getEffectiveModulePermission,
    canViewModule,
    canOperateModule,
    canManageModule,
    activeCondominioId,
    setActiveCondominioId,
    getDefaultCondominioId,
    getAvailableModulos,
    getUserSchedules,
    refreshSession,
    refreshAccessToken,
    getSessionRevision,
    isSessionCurrent,
    isAuthenticated: !!user,
    token: authToken,
  }), [
    user,
    loading,
    error,
    login,
    logout,
    api,
    hasRole,
    isDireccion,
    isSupervisor,
    isAdministrador,
    hasCondominioAccess,
    getEffectiveModulePermission,
    canViewModule,
    canOperateModule,
    canManageModule,
    activeCondominioId,
    setActiveCondominioId,
    getDefaultCondominioId,
    getAvailableModulos,
    getUserSchedules,
    refreshSession,
    refreshAccessToken,
    getSessionRevision,
    isSessionCurrent,
    authToken,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
